import { NavigatorScreenParams, useIsFocused } from '@react-navigation/native'
import React, { useEffect } from 'react'

import { useAuth } from '../../context/auth'
import { useSessionCart } from '../../context/cart'
import { RootStackParamList, RootStackScreenProps } from '../../navigation'
import { isUserAuthenticated } from '../../util/auth'

type Props = RootStackScreenProps<'AuthRedirect'>

type RouteConfig = {
  screen: string
  params?: { screen?: string; entityScreen?: string; params?: { variableName: string } }
}

const routesConfig: Record<string, RouteConfig> = {
  consultations: {
    screen: 'HistoryTab',
    params: { screen: 'Consultations', entityScreen: 'Consultation' },
  },
  orders: {
    screen: 'HomeTab',
    params: { screen: 'Order' },
  },
  'consultations/orders': {
    screen: 'HistoryTab',
    params: { screen: 'Order' },
  },
  products: {
    screen: 'HomeTab',
    params: { screen: 'Product', params: { variableName: 'slug' } },
  },
  groups: {
    screen: 'HomeTab',
    params: { screen: 'ProductGroup', params: { variableName: 'slug' } },
  },
  account: {
    screen: 'ProfileTab',
    params: { screen: 'Profile' },
  },
}

const continueNavigationParams = (urlPath = ''): Partial<NavigatorScreenParams<unknown>> | undefined => {
  const initialRouteParams = routesConfig[urlPath]
  const pathElements = urlPath.split('/')
  const entityId = !initialRouteParams && pathElements.length > 1 ? pathElements.pop() : undefined
  const collection = pathElements.join('/')
  const routeParams = initialRouteParams || routesConfig[collection] || routesConfig[pathElements[0]]

  if (!routeParams) return

  if (entityId && entityId !== 'welcome') {
    return {
      screen: routeParams.screen,
      params: {
        screen: routeParams.params?.entityScreen ?? routeParams.params?.screen,
        params: { [routeParams.params?.params?.variableName ?? 'id']: entityId },
      },
    } as unknown as Partial<NavigatorScreenParams<unknown>>
  }

  return {
    screen: routeParams.screen,
    params: routeParams.params,
  } as unknown as Partial<NavigatorScreenParams<unknown>>
}

export const AuthRedirect: React.FC<Props> = ({ route, navigation }) => {
  const { continueNavigationUrlPath, setContinueNavigationUrlPath, isNewAccount, isLoading, profile } = useAuth()
  const { product } = useSessionCart()
  const isFocused = useIsFocused()

  const getReferralParams = (urlPath = '') => {
    const [collection, slug] = urlPath.split('/')
    if (collection !== 'referral' || !slug) return

    return { name: 'Referral' as keyof RootStackParamList, params: { params: { slug } } }
  }

  useEffect(() => {
    async function init() {
      if (!isFocused) return
      if (isLoading) return

      const referralParams = getReferralParams(continueNavigationUrlPath)
      if (referralParams) return navigation.reset({ index: 0, routes: [referralParams] })

      if (!isUserAuthenticated({ profile })) {
        return navigation.reset({ index: 0, routes: [{ name: 'Welcome', params: { reason: route.params?.reason } }] })
      }

      const navigateParams = continueNavigationParams(continueNavigationUrlPath)

      if (navigateParams) {
        setContinueNavigationUrlPath()
        return navigation.reset({
          index: 0,
          routes: [{ name: 'Main', params: navigateParams }],
        })
      }

      return navigation.reset({
        index: 0,
        routes: [{ name: 'Main', params: { screen: 'HomeTab', params: { screen: 'HowCanWeHelp' } } }],
      })
    }

    init().then()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // We removed setContinueNavigationUrlPath and continueNavigationUrlPath from the dependencies array to avoid a re-render when
    // We redirect to the final screen when there are navigation params.
    isLoading,
    profile?.emrId,
    route.params,
    navigation,
    product,
    isNewAccount,
  ])

  return null
}

export default AuthRedirect
